import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";

// Create the authentication context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Set up axios interceptor for handling expired tokens
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // Auto logout if 401 response returned from API
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      // Clean up interceptor on unmount
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Check if user is logged in on initial load
  useEffect(() => {
    // Check if we have a token in localStorage
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (token && userData) {
      try {
        // Set user from localStorage
        setCurrentUser(JSON.parse(userData));

        // Add token to all future axios requests
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } catch (e) {
        console.error("Error parsing user data from localStorage", e);
        logout(); // Clear invalid data
      }
    }

    setLoading(false);
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setError("");
      setLoading(true);

      const response = await axios.post("/api/auth/login", { email, password });

      if (response.data.status === "success") {
        const { token, user } = response.data;

        // Save token and user to localStorage
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));

        // Set user in state
        setCurrentUser(user);

        // Add token to all future axios requests
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        return true;
      } else {
        setError(response.data.message || "Login failed");
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(
        error.response?.data?.message || "Login failed. Please try again."
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    // Remove token and user from localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    // Remove token from axios headers
    delete axios.defaults.headers.common["Authorization"];

    // Clear user from state
    setCurrentUser(null);
  };

  // Context value
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};
